import axios from "axios";
import { toast } from "sonner";
import { ROUTES } from "../constants/routes";
/**
 * -----------------------------
 * Refresh state control
 * -----------------------------
 */
let isRefreshing = false;
let queue = [];
/**
 * -----------------------------
 * Refresh Token Axios Instance
 * -----------------------------
 */
const authApi = axios.create({
    baseURL: "/",
    withCredentials: true,
});
/**
 * -----------------------------
 * Error handler (optional logging)
 * -----------------------------
 */
const errorCodeHandler = (error) => {
    const response = error.response;
    if (!response) {
        console.error("[NETWORK ERROR]", error.message);
        return Promise.reject(error);
    }
    const { data, status } = response;
    let message = "";
    if (data?.message) {
        message += data.message;
    }
    else {
        switch (status) {
            case 400:
                message += "Bad Request";
                break;
            case 401:
                message += "Unauthorized";
                break;
            case 403:
                message += "Forbidden";
                break;
            default:
                message += "Unknown error";
        }
    }
    toast.error(message);
    return Promise.reject(error);
};
/**
 * -----------------------------
 * Refresh Token Interceptor
 * -----------------------------
 */
function attachRefreshInterceptor(instance, redirectOnFailure) {
    instance.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;
        if (status === 403 && redirectOnFailure) {
            window.location.href = ROUTES.LOGIN();
            return Promise.reject(error);
        }
        if (status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            if (isRefreshing) {
                return new Promise((resolve) => {
                    queue.push(() => resolve(instance(originalRequest)));
                });
            }
            isRefreshing = true;
            try {
                await authApi.post("/auth/refresh");
                queue.forEach((cb) => cb());
                queue = [];
                return instance(originalRequest);
            }
            catch (refreshError) {
                if (redirectOnFailure) {
                    window.location.href = ROUTES.LOGIN();
                }
                return Promise.reject(refreshError);
            }
            finally {
                isRefreshing = false;
            }
        }
        return errorCodeHandler(error);
    });
}
/**
 * -----------------------------
 * Axios instances (with login redirect and without)
 * -----------------------------
 */
const api = axios.create({
    baseURL: "/",
    withCredentials: true,
});
const publicApi = axios.create({
    baseURL: "/",
    withCredentials: true,
});
attachRefreshInterceptor(api, true);
attachRefreshInterceptor(publicApi, false);
/**
 * -----------------------------
 * Request Interceptor (CSRF)
 * -----------------------------
 */
function attachCSRFInterceptor(instance) {
    instance.interceptors.request.use((config) => {
        const csrfToken = document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content");
        if (csrfToken) {
            config.headers = config.headers ?? {};
            config.headers["X-CSRF-Token"] = csrfToken;
        }
        return config;
    });
}
attachCSRFInterceptor(api);
attachCSRFInterceptor(publicApi);
/**
 * -----------------------------
 * Response helper
 * -----------------------------
 */
const responseBody = (response) => response.data;
/**
 * -----------------------------
 * Private API wrapper
 * -----------------------------
 */
export const privateRequest = {
    get: (url) => api.get(url).then(responseBody),
    post: (url, body) => api.post(url, body ?? {}).then(responseBody),
};
/**
 * -----------------------------
 * Public API wrapper
 * -----------------------------
 */
export const publicRequest = {
    get: (url) => publicApi.get(url).then(responseBody),
    post: (url, body) => publicApi.post(url, body ?? {}).then(responseBody),
};
